// src/utils/bluetoothUtils.js

/**
 * Data packet structure from Arduino:
 * - sensors[64]: uint16_t (128 bytes)
 * - imu[6]: int16_t (12 bytes) - ax, ay, az, gx, gy, gz
 * - onPointe: uint8_t (1 byte)
 * - turnFlag: uint8_t (1 byte)
 * - jumpFlag: uint8_t (1 byte)
 * Total: 143 bytes
 */

export const PACKET_SIZE = 142;

export const MESSAGE_TYPES = {
  DATA_PACKET: 'DATA_PACKET',
  TEXT_MESSAGE: 'TEXT_MESSAGE',
};

/**
 * Parse incoming BLE data - determines if it's a binary data packet or text message
 */
export const parseBluetoothData = (dataView) => {
  const byteLength = dataView.byteLength;
  
  // Ignore empty packets
  if (byteLength === 0) {
    console.warn('⚠️ Empty packet received');
    return null;
  }
  
  // Debug log
  //console.log(`📦 Received ${byteLength} bytes (expected data packet: ${PACKET_SIZE})`);
  
  // Check if it's a binary data packet
  // Allow for some flexibility (140-150 bytes) in case of padding
  if (byteLength >= 140 && byteLength <= 150) {
    return parseDataPacket(dataView);
  }
  
  // For smaller packets, check if it looks like binary or text
  // Binary sensor data will have lots of low bytes (0x00-0x0F)
  // Text will be printable ASCII (0x20-0x7E)
  if (byteLength > 50) {
    // Count how many bytes look like text vs binary
    let textLikeBytes = 0;
    let binaryLikeBytes = 0;
    const checkLength = Math.min(byteLength, 20);
    
    for (let i = 0; i < checkLength; i++) {
      const byte = dataView.getUint8(i);
      if (byte >= 0x20 && byte <= 0x7E) {
        textLikeBytes++;
      } else if (byte < 0x20) {
        binaryLikeBytes++;
      }
    }
    
    // If more binary-like, treat as data packet
    if (binaryLikeBytes > textLikeBytes) {
      console.log(`📊 Detected as data packet (binary-like: ${binaryLikeBytes}, text-like: ${textLikeBytes})`);
      return parseDataPacket(dataView);
    }
  }
  
  // Otherwise, treat as text message
  return parseTextMessage(dataView);
};

/**
 * Parse binary data packet from Arduino
 */
export const parseDataPacket = (dataView) => {
  const byteLength = dataView.byteLength;
  
  // Verify we have enough data for sensors at minimum
  if (byteLength < 128) {
    console.warn('⚠️ Packet too small for sensor data:', byteLength);
    return null;
  }
  
  const sensorValues = [];
  
  // Parse 64 sensor values (each is 2 bytes, little-endian)
  for (let i = 0; i < 64; i++) {
    const value = dataView.getUint16(i * 2, true);
    sensorValues.push(value);
  }
  
  // Parse IMU data (6 x int16_t starting at byte 128)
  let imu = { ax: 0, ay: 0, az: 0, gx: 0, gy: 0, gz: 0 };
  let onPointe = false;
  // let turnFlag = false;
  // let jumpFlag = false;
  
  if (byteLength >= 140) {
    imu = {
      ax: dataView.getInt16(128, true) / 1000.0,
      ay: dataView.getInt16(130, true) / 1000.0,
      az: dataView.getInt16(132, true) / 1000.0,
      gx: dataView.getInt16(134, true) / 10.0,
      gy: dataView.getInt16(136, true) / 10.0,
      gz: dataView.getInt16(138, true) / 10.0,
    };
  }
  
  if (byteLength >= 141) {
    onPointe = dataView.getUint8(140) === 1;
  }
  
  return {
    type: MESSAGE_TYPES.DATA_PACKET,
    sensors: sensorValues,
    imu,
    onPointe,
    timestamp: Date.now(),
  };
};

/**
 * Parse text message from Arduino
 */
export const parseTextMessage = (dataView) => {
  const decoder = new TextDecoder('utf-8');
  const bytes = new Uint8Array(dataView.buffer, dataView.byteOffset, dataView.byteLength);
  const message = decoder.decode(bytes).trim();
  
  console.log('📨 Received message:', message);
  
  // Parse structured messages
  const parsed = parseStructuredMessage(message);
  
  return {
    type: MESSAGE_TYPES.TEXT_MESSAGE,
    raw: message,
    ...parsed,
    timestamp: Date.now(),
  };
};

/**
 * Parse structured messages from Arduino (e.g., "STATUS:G128:M2000:...")
 */
export const parseStructuredMessage = (message) => {
  // Connection messages
  if (message === 'CONNECTED') {
    return { event: 'connected' };
  }
  if (message === 'DEVICE_READY') {
    return { event: 'deviceReady' };
  }
  if (message === 'PONG') {
    return { event: 'pong' };
  }
  
  // Pointe state messages
  if (message === 'ON_POINTE') {
    return { event: 'pointeChange', onPointe: true };
  }
  if (message === 'FLAT') {
    return { event: 'pointeChange', onPointe: false };
  }
  
  // Jump detection
  if (message.startsWith('JUMP:')) {
    const magnitude = parseFloat(message.split(':')[1]);
    return { event: 'jump', magnitude };
  }
  
  // Turn detection
  if (message === 'TURN') {
    return { event: 'turn' };
  }
  
  // Calibration messages
  if (message.startsWith('CAL_')) {
    return parseCalibrationMessage(message);
  }
  
  // Status message
  if (message.startsWith('STATUS:')) {
    return parseStatusMessage(message);
  }
  
  // Gain messages
  if (message.startsWith('GAIN_RESET:')) {
    const gain = parseInt(message.split(':')[1]);
    return { event: 'gainReset', gain };
  }
  if (message.startsWith('FLAT_GAIN_ADJ:') || message.startsWith('POINTE_GAIN_ADJ:')) {
    const parts = message.split(':');
    return {
      event: 'gainAdjust',
      phase: parts[0].includes('FLAT') ? 'flat' : 'pointe',
      gain: parseInt(parts[1]),
      reading: parseInt(parts[2]),
    };
  }
  if (message.startsWith('FLAT_GAIN_DONE:') || message.startsWith('POINTE_GAIN_DONE:')) {
    const parts = message.split(':');
    return {
      event: 'gainDone',
      phase: parts[0].includes('FLAT') ? 'flat' : 'pointe',
      gain: parseInt(parts[1]),
      reading: parseInt(parts[2]),
    };
  }
  
  // IMU calibration messages
  if (message.startsWith('FLAT_IMU_DONE:') || message.startsWith('POINTE_IMU_DONE:')) {
    const parts = message.split(':');
    return {
      event: 'imuCalDone',
      phase: parts[0].includes('FLAT') ? 'flat' : 'pointe',
      az: parseFloat(parts[1]),
      ay: parseFloat(parts[2]),
    };
  }
  
  // IMU init messages
  if (message === 'IMU_INIT_OK') {
    return { event: 'imuInit', success: true };
  }
  if (message === 'IMU_INIT_FAIL') {
    return { event: 'imuInit', success: false };
  }
  
  // Error messages
  if (message.startsWith('ERROR:')) {
    return { event: 'error', error: message.substring(6) };
  }
  
  // Unknown message
  return { event: 'unknown', message };
};

/**
 * Parse calibration-specific messages
 */
const parseCalibrationMessage = (message) => {
  if (message === 'CAL_CANCELLED') {
    return { event: 'calibration', state: 'cancelled' };
  }
  
  if (message.startsWith('CAL_START:')) {
    return { event: 'calibration', state: 'start', instruction: message.split(':')[1] };
  }
  
  if (message.startsWith('CAL_FLAT_IMU:')) {
    return { event: 'calibration', state: 'flatImu', instruction: message.split(':')[1] };
  }
  
  if (message.startsWith('CAL_NEXT:')) {
    return { event: 'calibration', state: 'waitPointe', instruction: message.split(':')[1] };
  }
  
  if (message.startsWith('CAL_POINTE_GAIN:')) {
    return { event: 'calibration', state: 'pointeGain', instruction: message.split(':')[1] };
  }
  
  if (message.startsWith('CAL_POINTE_IMU:')) {
    return { event: 'calibration', state: 'pointeImu', instruction: message.split(':')[1] };
  }
  
  if (message.startsWith('CAL_COMPLETE:')) {
    // Format: CAL_COMPLETE:G128:TZ0.40:TY0.70
    const parts = message.split(':');
    return {
      event: 'calibration',
      state: 'complete',
      gain: parseInt(parts[1].substring(1)),
      thresholdZ: parseFloat(parts[2].substring(2)),
      thresholdY: parseFloat(parts[3].substring(2)),
    };
  }
  
  return { event: 'calibration', state: 'unknown', raw: message };
};

/**
 * Parse status message
 * Format: STATUS:G128:M2000:CAL0:P0:TZ0.40:TY0.70
 */
const parseStatusMessage = (message) => {
  const parts = message.split(':');
  
  return {
    event: 'status',
    gain: parseInt(parts[1].substring(1)),
    maxReading: parseInt(parts[2].substring(1)),
    calibrationState: parseInt(parts[3].substring(3)),
    onPointe: parts[4].substring(1) === '1',
    thresholdZ: parseFloat(parts[5].substring(2)),
    thresholdY: parseFloat(parts[6].substring(2)),
  };
};

// ==================== Command Sending Functions ====================

/**
 * Create a command to send to Arduino
 */
export const createCommand = (command) => {
  const encoder = new TextEncoder();
  return encoder.encode(command);
};

/**
 * Available commands
 */
export const COMMANDS = {
  // Start full calibration
  START_CALIBRATION: 'C',
  
  // Continue to pointe phase (after flat calibration)
  CONTINUE_POINTE: 'CP',
  
  // Cancel calibration
  CANCEL_CALIBRATION: 'X',
  
  // Reset gain to default (128)
  RESET_GAIN: 'R',
  
  // Request status
  GET_STATUS: 'S',
  
  // Ping (test connection)
  PING: 'P',
};

/**
 * Send a command via BLE characteristic
 */
export const sendCommand = async (characteristic, command) => {
  console.log('📤 ========== SEND COMMAND ==========');
  console.log('📤 characteristic:', characteristic);
  console.log('📤 command:', command);
  console.log('📤 command type:', typeof command);
  console.log('📤 command length:', command?.length);
  
  if (!characteristic) {
    console.error('❌ No characteristic!');
    throw new Error('No characteristic available');
  }
  
  // Encode the command
  const encoder = new TextEncoder();
  const data = encoder.encode(command);
  
  console.log('📤 Encoded data:', data);
  console.log('📤 Encoded bytes:', Array.from(data));
  console.log('📤 Byte length:', data.byteLength);
  
  // Check properties
  const props = characteristic.properties;
  console.log('📤 Characteristic properties:', {
    write: props?.write,
    writeWithoutResponse: props?.writeWithoutResponse,
    read: props?.read,
    notify: props?.notify
  });
  
  try {
    if (props?.writeWithoutResponse) {
      console.log('📤 Calling writeValueWithoutResponse...');
      await characteristic.writeValueWithoutResponse(data);
      console.log('✅ writeValueWithoutResponse completed');
    } else if (props?.write) {
      console.log('📤 Calling writeValue...');
      await characteristic.writeValue(data);
      console.log('✅ writeValue completed');
    } else {
      throw new Error('Characteristic does not support write');
    }
    
    console.log('📤 ========== COMMAND SENT ==========');
    return true;
  } catch (error) {
    console.error('❌ Write failed:', error);
    console.error('❌ Error name:', error.name);
    console.error('❌ Error message:', error.message);
    throw error;
  }
};

/**
 * Send calibration start command
 */
export const startCalibration = (characteristic) => {
  return sendCommand(characteristic, COMMANDS.START_CALIBRATION);
};

/**
 * Send continue to pointe command
 */
export const continueToPointe = (characteristic) => {
  return sendCommand(characteristic, COMMANDS.CONTINUE_POINTE);
};

/**
 * Send cancel calibration command
 */
export const cancelCalibration = (characteristic) => {
  return sendCommand(characteristic, COMMANDS.CANCEL_CALIBRATION);
};

/**
 * Send reset gain command
 */
export const resetGain = (characteristic) => {
  return sendCommand(characteristic, COMMANDS.RESET_GAIN);
};

/**
 * Send status request command
 */
export const getStatus = (characteristic) => {
  return sendCommand(characteristic, COMMANDS.GET_STATUS);
};

/**
 * Send ping command
 */
export const ping = (characteristic) => {
  return sendCommand(characteristic, COMMANDS.PING);
};

// ==================== Utility Functions ====================

/**
 * Check if Web Bluetooth is supported
 */
export const checkBluetoothSupport = () => {
  if (!navigator.bluetooth) {
    throw new Error('Web Bluetooth API is not supported in this browser');
  }
};

/**
 * BLE Service and Characteristic UUIDs (must match Arduino)
 */
export const BLE_CONFIG = {
  SERVICE_UUID: '4fafc201-1fb5-459e-8fcc-c5c9c331914b',
  CHARACTERISTIC_UUID: 'beb5483e-36e1-4688-b7f5-ea07361b26a8',
  DEVICE_NAME: 'PSCore3',
};

/**
 * Calibration states (matching Arduino enum)
 */
export const CALIBRATION_STATES = {
  IDLE: 0,
  FLAT_GAIN: 1,
  FLAT_IMU: 2,
  WAIT_POINTE: 3,
  POINTE_GAIN: 4,
  POINTE_IMU: 5,
  COMPLETE: 6,
};

/**
 * Get human-readable calibration state name
 */
export const getCalibrationStateName = (state) => {
  const names = {
    [CALIBRATION_STATES.IDLE]: 'Idle',
    [CALIBRATION_STATES.FLAT_GAIN]: 'Calibrating Flat Gain',
    [CALIBRATION_STATES.FLAT_IMU]: 'Calibrating Flat IMU',
    [CALIBRATION_STATES.WAIT_POINTE]: 'Waiting for Pointe',
    [CALIBRATION_STATES.POINTE_GAIN]: 'Calibrating Pointe Gain',
    [CALIBRATION_STATES.POINTE_IMU]: 'Calibrating Pointe IMU',
    [CALIBRATION_STATES.COMPLETE]: 'Complete',
  };
  return names[state] || 'Unknown';
};